import { z } from 'zod';
import { ContentVisibility } from '@sphere/shared/domain';
import { ActivityVisibility, DifficultyLevel } from '@sphere/learning/domain';

// --- Helpers ---

const jsonStringSchema = (errorMessage: string) =>
  z
    .string()
    .refine(
      (val) => {
        try {
          JSON.parse(val);
          return true;
        } catch {
          return false;
        }
      },
      { message: errorMessage }
    )
    .default('{}');

/**
 * Transforme une string séparée par virgules en tableau de strings,
 * ou garde un tableau tel quel.
 * Permet l'input utilisateur comme "tag1, tag2" → ["tag1", "tag2"]
 */
const tagsSchema = z.preprocess(
  (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      return val
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
    }
    return [];
  },
  z.array(z.string()).optional()
);

// --- Learning Paths ---

export const learningPathSchema = z.object({
  title: z
    .string()
    .min(3, 'Le titre doit contenir au moins 3 caractères')
    .max(100, 'Le titre ne doit pas dépasser 100 caractères'),
  description: z.string().optional(),
  subjectExternalId: z.string().min(1, 'La matière est requise'),
  classLevelExternalId: z.string().min(1, 'Le niveau est requis'),
  // Pas de .default() ici : sinon z.input rend visibility optionnelle et le
  // typage de zodResolver ne s'aligne plus avec z.infer (react-hook-form).
  visibility: z.nativeEnum(ContentVisibility),
  establishmentId: z.string().optional(),
});

export type LearningPathFormData = z.infer<typeof learningPathSchema>;

// --- Activities ---

export const activitySchema = z.object({
  title: z
    .string()
    .min(3, 'Le titre doit contenir au moins 3 caractères')
    .max(100, 'Le titre ne doit pas dépasser 100 caractères'),
  description: z.string().optional(),
  typeId: z.string().min(1, "Le type d'activité est requis"),
  subjectExternalId: z.string().optional(), // Peut être optionnel selon le type
  classLevelExternalId: z.string().optional(),
  visibility: z.nativeEnum(ActivityVisibility).default(ActivityVisibility.PRIVATE),
  difficulty: z.nativeEnum(DifficultyLevel).default(DifficultyLevel.MEDIUM),
  estimatedMinutes: z.coerce.number().min(0).optional(),
  tags: tagsSchema,
  stateId: z.string().min(1, "L'état est requis"),
  instructions: z.string().optional(),
  content: jsonStringSchema('Le contenu doit être un JSON valide'),
  config: jsonStringSchema('La configuration doit être un JSON valide'),
});

export type ActivityFormData = z.infer<typeof activitySchema>;

// --- Activity Types ---

export const activityTypeSchema = z.object({
  code: z.string().min(2, 'Le code doit contenir au moins 2 caractères').max(64),
  label: z.string().min(1, 'Le libellé est requis').max(255),
  description: z.string().optional(),
  jsonSchema: jsonStringSchema('Le schéma JSON doit être un JSON valide'),
  defaultConfig: jsonStringSchema('La configuration doit être un JSON valide'),
  supportsAutoGrading: z.boolean().default(true),
  supportsRetries: z.boolean().default(true),
  maxAttempts: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z.number().min(1).max(999).optional()
  ),
  icon: z.string().max(64).optional(),
  color: z.string().max(7).optional(),
  category: z.string().max(64).optional(),
  isActive: z.boolean().default(true),
});

export type ActivityTypeFormData = z.infer<typeof activityTypeSchema>;

// --- Activity Templates ---

export const activityTemplateSchema = z.object({
  typeId: z.string().min(1, "Le type d'activité est requis"),
  name: z.string().min(1, 'Le nom est requis').max(255),
  description: z.string().optional(),
  tags: tagsSchema,
  seedContent: jsonStringSchema('Le contenu doit être un JSON valide'),
  config: jsonStringSchema('La configuration doit être un JSON valide'),
  estimatedMinutes: z.coerce.number().min(0).optional(),
  visibility: z.nativeEnum(ActivityVisibility).default(ActivityVisibility.PRIVATE),
});

export type ActivityTemplateFormData = z.infer<typeof activityTemplateSchema>;

// --- Activity Categories ---

export const activityCategorySchema = z.object({
  code: z.string().min(2, 'Le code doit contenir au moins 2 caractères').max(64),
  label: z.string().min(1, 'Le libellé est requis').max(255),
  description: z.string().optional(),
  requiresGrading: z.boolean(),
  icon: z.string().max(64).optional(),
  color: z.string().max(7).optional(),
  suggestedDuration: z.coerce.number().min(1).optional(),
});

export type ActivityCategoryFormData = z.infer<typeof activityCategorySchema>;
